import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info, ArrowLeft, ChevronDown, Search, Users, Menu, X, Instagram } from "lucide-react";

const SITE = "https://acaglobal.my";

const NAV_LINKS = [
  { label: "Home", href: `${SITE}/` },
  { label: "About Us", href: `${SITE}/about` },
  { label: "ACA Program", href: `${SITE}/program` },
  { label: "Membership", href: `${SITE}/membership` },
  { label: "Training", href: `${SITE}/training` },
  { label: "Contact", href: `${SITE}/contact` },
];

const FOOTER_COLS = [
  { title: "About", links: [
    { label: "President's Message", href: `${SITE}/about#president` },
    { label: "Our Patron", href: `${SITE}/about#patron` },
    { label: "Council Members", href: `${SITE}/about#council` },
    { label: "Committees", href: `${SITE}/about#committees` },
    { label: "Vision & Mission", href: `${SITE}/about#vision` },
  ] },
  { title: "Program", links: [
    { label: "Membership Grades", href: `${SITE}/program#grades` },
    { label: "Certified Program", href: `${SITE}/program#stages` },
    { label: "Advanced Certs", href: `${SITE}/program#advanced` },
    { label: "Sample Certificate", href: `${SITE}/program#certificate` },
  ] },
  { title: "Membership", links: [
    { label: "Benefits", href: `${SITE}/membership#benefits` },
    { label: "Fee Structure", href: `${SITE}/membership#fees` },
    { label: "How to Join", href: `${SITE}/membership#join` },
    { label: "IPA Australia MRA", href: `${SITE}/membership#mra` },
  ] },
  { title: "Contact", links: [
    { label: "+603-7495 9056", href: "tel:+60374959056" },
    { label: "admin@certified-accountants.org.my", href: "mailto:admin@certified-accountants.org.my" },
    { label: "Training & Forums", href: `${SITE}/training` },
  ] },
];

const SOCIALS = [
  { label: "Facebook", href: "https://www.facebook.com/CertifiedAccountantsMalaysia/", icon: "f" },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/association-of-certified-accounts-malaysia/", icon: "in" },
  { label: "Instagram", href: "https://www.instagram.com/aca_global_/", icon: <Instagram className="h-4 w-4" /> },
];

type Member = {
  id: string;
  full_name: string;
  city: string | null;
  country: string | null;
  admission_year: number | null;
  qualification: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  payment_date: string | null;
};

type SortKey = "full_name" | "created_at";
type SortDir = "asc" | "desc";

const FindMember = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [lastNameError, setLastNameError] = useState(false);
  const [results, setResults] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchedName, setSearchedName] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("full_name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);
  const [menuOpen, setMenuOpen] = useState(false);
  const perPage = 10;

  const handleSearch = async () => {
    if (!lastName.trim()) {
      setLastNameError(true);
      return;
    }
    setLastNameError(false);
    const name = `${firstName.trim()} ${lastName.trim()}`.trim();
    setSearchedName(name);
    setLoading(true);
    setSearched(false);
    setPage(1);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL || "/api";
      const { data: res } = await axios.get(`${baseURL}/members/search`, {
        params: { name: lastName.trim(), ...(firstName.trim() && { firstName: firstName.trim() }) },
      });
      setResults(res.data || []);
    } catch {
      setResults([]);
    }
    setSearched(true);
    setLoading(false);
  };

  const handleRefine = () => {
    setSearched(false);
    setResults([]);
  };

  const sorted = [...results].sort((a, b) => {
    const valA = sortKey === "full_name" ? a.full_name : a.created_at;
    const valB = sortKey === "full_name" ? b.full_name : b.created_at;
    return sortDir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paged = sorted.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="min-h-screen bg-white flex flex-col font-['Inter',sans-serif] text-[#1e2a3a]">

      {/* ── Header (matches acaglobal.my) ── */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#e5e7eb] shadow-sm">
        <div className="mx-auto flex h-[70px] max-w-[1400px] items-center justify-between px-5">
          <a href={SITE} className="flex items-center">
            <span className="text-[2rem] font-extrabold leading-none tracking-[-0.04em]">
              <span className="text-[#1B2F5E]">AC</span><span className="text-[#C8102E]">A</span>
            </span>
            <span className="mx-[0.9rem] h-8 w-px bg-[#e5e7eb]" />
          </a>
          <nav className="hidden min-[900px]:flex items-center gap-0.5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="group relative whitespace-nowrap rounded px-[0.55rem] py-2 text-[0.78rem] font-medium text-[#374151] transition-colors hover:text-[#1B2F5E] xl:px-[0.9rem] xl:text-[0.83rem]"
              >
                {l.label}
                <span className="absolute bottom-0.5 left-[0.55rem] right-[0.55rem] h-0.5 origin-left scale-x-0 bg-[#C8102E] transition-transform group-hover:scale-x-100 xl:left-[0.9rem] xl:right-[0.9rem]" />
              </a>
            ))}
            <Link
              to="/find-member"
              className="ml-1.5 whitespace-nowrap rounded border-[1.5px] border-[#1B2F5E] bg-[#1B2F5E] px-[0.95rem] py-[0.42rem] text-[0.78rem] font-semibold text-white xl:text-[0.83rem]"
            >
              Find Accountant
            </Link>
            <Link
              to="/login"
              className="ml-1 whitespace-nowrap rounded bg-[#C8102E] px-5 py-2 text-[0.78rem] font-semibold text-white transition-colors hover:bg-[#a00d24] xl:text-[0.82rem]"
            >
              Member Login
            </Link>
          </nav>
          <button
            className="min-[900px]:hidden p-2 text-[#1B2F5E]"
            aria-label="Toggle menu"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <nav className="min-[900px]:hidden fixed inset-x-0 top-[70px] bottom-0 z-40 flex flex-col overflow-y-auto border-t border-[#e5e7eb] bg-white px-5 py-6">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} className="border-b border-[#f3f4f6] py-4 text-base font-medium text-[#374151] hover:text-[#C8102E]">
                {l.label}
              </a>
            ))}
            <Link to="/find-member" onClick={() => setMenuOpen(false)} className="mt-6 rounded-md border-2 border-[#1B2F5E] bg-[#1B2F5E] p-4 text-center font-bold text-white">
              Find Accountant
            </Link>
            <Link to="/login" className="mt-3 rounded-md bg-[#C8102E] p-4 text-center font-bold text-white">
              Member Login
            </Link>
          </nav>
        )}
      </header>

      {/* ── Page hero (matches inner pages of acaglobal.my) ── */}
      <section className="relative overflow-hidden bg-[linear-gradient(110deg,#0f1c3a_0%,#1B2F5E_60%,#2a1030_100%)] pb-10 pt-10 sm:pb-14 sm:pt-16">
        <div className="absolute left-0 top-0 h-full w-1 bg-[#C8102E]" />
        <div className="pointer-events-none absolute -right-28 -top-28 h-[480px] w-[480px] rounded-full border border-white/[0.04]" />
        <div className="relative mx-auto max-w-[1160px] px-5">
          <div className="mb-4 flex items-center gap-1.5 text-xs text-white/50">
            <a href={SITE} className="transition-colors hover:text-white">Home</a>
            <span className="text-white/30">/</span>
            {searched ? (
              <>
                <button onClick={handleRefine} className="transition-colors hover:text-white">Find Accountant</button>
                <span className="text-white/30">/</span>
                <span className="text-white/80">Search results</span>
              </>
            ) : (
              <span className="text-white/80">Find Accountant</span>
            )}
          </div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#C8102E]/40 bg-[#C8102E]/10 px-4 py-1.5 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-[#f4b6c0]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C8102E]" />
            Member Directory
          </div>
          <h1 className="mb-3 max-w-[640px] font-['Playfair_Display',serif] text-[clamp(1.9rem,4vw,3rem)] font-bold leading-[1.15] text-white">
            Find an ACA <span className="text-[#f4a3b0]">Accountant</span>
          </h1>
          <p className="max-w-[560px] text-[0.95rem] leading-[1.85] text-white/70">
            Search the register of the Association of Certified Accountants, Malaysia to verify a certified ACA member.
          </p>
        </div>
      </section>

      {/* ── Page body ── */}
      <div className="flex-1 bg-[#f8f7f5]">
        <div className="mx-auto max-w-[1160px] px-4 py-8 sm:px-5 sm:py-12 lg:py-14">

          {!searched ? (
            /* ── Search Form ── */
            <div className="w-full rounded-xl border border-[#e5e7eb] bg-white p-5 shadow-[0_4px_16px_rgba(27,47,94,.1)] sm:p-8 lg:p-10">
              <div className="mb-3 flex items-center gap-2 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[#C8102E]">
                <span className="h-0.5 w-6 bg-[#C8102E]" />
                Search the register
              </div>
              <h2 className="mb-1 font-['Playfair_Display',serif] text-[1.6rem] font-bold leading-tight text-[#1B2F5E] sm:text-[1.9rem]">Find an ACA member</h2>
              <p className="mb-7 text-[0.95rem] text-[#6b7280]">Search by name to find an ACA member.</p>

              {/* Notice */}
              <div className="mb-8 flex items-start gap-3 rounded border-l-4 border-[#C8102E] bg-[#f8e6e9] px-4 py-3.5">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#C8102E]" />
                <p className="text-sm leading-snug text-[#1e2a3a]">
                  Please note, new entries and updates can take up to 24 hours to appear.
                </p>
              </div>

              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-semibold text-[#1B2F5E]">First name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="h-12 rounded border-[#e5e7eb] bg-white focus-visible:border-[#1B2F5E] focus-visible:ring-[#1B2F5E]/20"
                    placeholder="Enter first name"
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-sm font-semibold text-[#1B2F5E]">
                    Last name <span className="text-[#C8102E]">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => { setLastName(e.target.value); if (e.target.value.trim()) setLastNameError(false); }}
                    className={`h-12 rounded border-[#e5e7eb] bg-white focus-visible:border-[#1B2F5E] focus-visible:ring-[#1B2F5E]/20 ${lastNameError ? "border-[#C8102E]" : ""}`}
                    placeholder="Enter last name"
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  {lastNameError && (
                    <div className="mt-1 flex items-center gap-2 text-xs text-[#C8102E]">
                      <Info className="h-3.5 w-3.5 shrink-0" />
                      A last name must be provided
                    </div>
                  )}
                </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className="inline-flex min-h-[3.25rem] w-full items-center justify-center gap-2 rounded bg-[#C8102E] px-9 sm:w-auto text-[0.95rem] font-semibold text-white transition-all hover:-translate-y-px hover:bg-[#a00d24] hover:shadow-[0_6px_20px_rgba(200,16,46,.3)] disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Searching…
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4" />
                        Find member
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

          ) : (
            /* ── Results ── */
            <div className="animate-fade-in">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-['Playfair_Display',serif] text-[1.6rem] font-bold text-[#1B2F5E] sm:text-[1.9rem]">Search results</h2>
                <button
                  onClick={handleRefine}
                  className="flex items-center gap-1.5 rounded border-2 border-[#1B2F5E] px-4 py-2 text-sm font-semibold text-[#1B2F5E] transition-colors hover:bg-[#1B2F5E] hover:text-white"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Refine search
                </button>
              </div>

              <p className="mb-6 text-sm text-[#6b7280]">
                Your search for ACA members named{" "}
                <strong className="text-[#1B2F5E]">"{searchedName}"</strong>{" "}
                returned the following results:
              </p>

              <div className="mb-6 h-px bg-[#e5e7eb]" />

              {/* Sort + count row */}
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-[#1B2F5E]">Sort by</span>
                  <div className="relative">
                    <select
                      value={`${sortKey}_${sortDir}`}
                      onChange={(e) => {
                        const parts = e.target.value.split("_");
                        const dir = parts.pop() as SortDir;
                        const key = parts.join("_") as SortKey;
                        setSortKey(key);
                        setSortDir(dir);
                      }}
                      className="cursor-pointer appearance-none rounded border border-[#e5e7eb] bg-white px-3 py-1.5 pr-8 text-sm text-[#1e2a3a] focus:border-[#1B2F5E] focus:outline-none"
                    >
                      <option value="full_name_asc">Full name (ascending)</option>
                      <option value="full_name_desc">Full name (descending)</option>
                      <option value="created_at_asc">Admission year (ascending)</option>
                      <option value="created_at_desc">Admission year (descending)</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#6b7280]" />
                  </div>
                </div>
                <span className="text-sm text-[#6b7280]">
                  {results.length === 0
                    ? "No results"
                    : `Displaying ${(page - 1) * perPage + 1}–${Math.min(page * perPage, results.length)} of ${results.length}`}
                </span>
              </div>

              {results.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#d1d5db] bg-white py-20 text-center">
                  <Users className="mx-auto mb-3 h-10 w-10 text-[#1B2F5E]/25" />
                  <p className="text-sm font-medium text-[#4b5563]">No ACA members found matching "{searchedName}"</p>
                  <p className="mt-1 text-xs text-[#9ca3af]">Try a different name or check your spelling</p>
                </div>
              ) : (
                <>
                  {/* Cards on phones */}
                  <div className="space-y-3 md:hidden">
                    {paged.map((m) => {
                      const certDate = m.payment_date || m.updated_at;
                      const certFormatted = certDate
                        ? new Date(certDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                        : "—";
                      return (
                        <div key={m.id} className="rounded-xl border border-[#e5e7eb] bg-white p-4 shadow-sm">
                          <div className="mb-3 flex items-center gap-3 border-b border-[#f3f4f6] pb-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1B2F5E]">
                              <span className="text-xs font-bold text-white">{m.full_name.charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                              <div className="break-words font-semibold text-[#1B2F5E]">{m.full_name}</div>
                              <div className="mt-0.5 text-xs font-bold text-[#C8102E]">ACA</div>
                            </div>
                          </div>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <dt className="text-[#6b7280]">City/town</dt>
                            <dd className="text-right">{m.city || "—"}</dd>
                            <dt className="text-[#6b7280]">Location</dt>
                            <dd className="text-right">{m.country || "—"}</dd>
                            <dt className="text-[#6b7280]">Admission year</dt>
                            <dd className="text-right font-medium">{m.admission_year ?? new Date(m.created_at).getFullYear()}</dd>
                            <dt className="text-[#6b7280]">Certificates</dt>
                            <dd className="text-right">{certFormatted}</dd>
                          </dl>
                        </div>
                      );
                    })}
                  </div>

                  <div className="hidden overflow-x-auto rounded-xl md:block border border-[#e5e7eb] bg-white shadow-[0_4px_16px_rgba(27,47,94,.1)]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#1B2F5E] text-white">
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Full name</th>
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">City/town</th>
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Location</th>
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Admission year</th>
                          <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Certificates/licences held</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#f3f4f6]">
                        {paged.map((m) => {
                          const certDate = m.payment_date || m.updated_at;
                          const certFormatted = certDate
                            ? new Date(certDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                            : "—";
                          return (
                            <tr key={m.id} className="transition-colors hover:bg-[#f9fafb]">
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1B2F5E]">
                                    <span className="text-xs font-bold text-white">
                                      {m.full_name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <div className="font-semibold text-[#1B2F5E]">{m.full_name}</div>
                                    <div className="mt-0.5 text-xs font-bold text-[#C8102E]">ACA</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-4">{m.city || "—"}</td>
                              <td className="px-5 py-4">{m.country || "—"}</td>
                              <td className="px-5 py-4 font-medium">
                                {m.admission_year ?? new Date(m.created_at).getFullYear()}
                              </td>
                              <td className="px-5 py-4">{certFormatted}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
                      <span className="text-xs text-[#6b7280]">
                        Page {page} of {totalPages}
                      </span>
                      <div className="flex flex-wrap items-center justify-center gap-1.5">
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="rounded border border-[#e5e7eb] bg-white px-4 py-1.5 text-sm transition-colors hover:bg-[#f3f4f6] disabled:opacity-40"
                        >
                          Previous
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            className={`h-8 w-8 rounded border text-sm transition-colors ${
                              p === page
                                ? "border-transparent bg-[#C8102E] text-white"
                                : "border-[#e5e7eb] bg-white text-[#1e2a3a] hover:bg-[#f3f4f6]"
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                        <button
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="rounded border border-[#e5e7eb] bg-white px-4 py-1.5 text-sm transition-colors hover:bg-[#f3f4f6] disabled:opacity-40"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Footer (matches acaglobal.my) ── */}
      <footer className="bg-[#0a1628] text-white/65">
        <div className="mx-auto max-w-[1160px] px-4 sm:px-5">
          <div className="grid grid-cols-1 gap-10 pb-10 pt-12 lg:grid-cols-[2fr_3fr] lg:pt-14">
            <div>
              <div className="mb-4 flex items-center gap-3">
                <span className="text-[1.8rem] font-extrabold tracking-[-0.04em]">
                  <span className="text-white">AC</span><span className="text-[#C8102E]">A</span>
                </span>
                <span className="h-7 w-px bg-white/[0.12]" />
                <span className="text-[0.58rem] font-semibold uppercase leading-normal tracking-[0.1em] text-white/35">
                  Akauntan<br />Bertauliah<br />Malaysia
                </span>
              </div>
              <p className="mb-3 text-[0.82rem] leading-[1.85]">
                The Association of Certified Accountants, Malaysia — developing and upgrading accountants through internationally recognised qualifications since 1980.
              </p>
              <p className="mb-3 text-[0.7rem] text-white/30">Registration No: PPM-018-14-09091980</p>
              <Link to="/find-member" className="mt-1 inline-flex rounded bg-[#C8102E] px-4 py-2 text-[0.82rem] font-semibold text-white hover:bg-[#a00d24]">
                Find Accountant
              </Link>
              <div className="mt-5 flex gap-3">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-sm font-semibold text-white/70 transition-colors hover:border-[#C8102E] hover:bg-[#C8102E] hover:text-white"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
              {FOOTER_COLS.map((col) => (
                <div key={col.title}>
                  <h5 className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-[#C8102E]">{col.title}</h5>
                  <ul className="flex flex-col gap-2">
                    {col.links.map((l) => (
                      <li key={l.label}>
                        <a href={l.href} className="break-all text-[0.82rem] sm:break-words leading-snug text-white/50 transition-colors hover:text-white">{l.label}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 border-t border-white/[0.06] py-4 text-xs text-white/30 sm:flex-row sm:justify-between">
            <span>© {new Date().getFullYear()} Association of Certified Accountants, Malaysia. All rights reserved.</span>
            <span>Privacy Policy &nbsp;·&nbsp; Terms of Use</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FindMember;
